import {
	canCheckoutEventAfterEndDate,
	EventInstanceDto,
	EventUserRolesEnum,
	getEventPriceRangeString,
	isEventPast,
	UserDto,
} from '@sweatpals/shared';
import { useMemo } from 'react';
import { EventType } from 'services/types/event-types';
import useLink from '../../utils/hooks/useLink';
import { HOME_REDIRECT_URL } from '../../constants';

export interface EventAdditionalDataProps {
	eventData?: EventType;
	eventInstances?: EventType[];
	currentUser?: UserDto;
}

export const useEventAdditionalData = (props: EventAdditionalDataProps) => {
	const { eventData, currentUser, eventInstances } = props;

	const { makeEventLink, makeHostLink } = useLink();

	const eventId = useMemo(() => eventData?.baseId ?? eventData?.id, [eventData?.baseId, eventData?.id]);
	const instance = useMemo(
		() => (eventData?.instance || eventData?.startDate ? new Date(eventData.instance ?? eventData.startDate).toISOString() : undefined),
		[eventData?.instance, eventData?.startDate]
	);

	const eventAlias = useMemo(() => eventData?.alias, [eventData]);
	const shortLocalInstance = useMemo(() => eventData?.shortLocalInstance, [eventData]);

	const eventPageUrl = useMemo(
		() =>
			eventData ? makeEventLink({
				eventAlias: eventData?.alias,
				shortLocalInstance: eventInstances && eventInstances?.length > 1 ? eventData?.shortLocalInstance : undefined,
			}) : '',
		[makeEventLink, eventData?.alias, eventData?.shortLocalInstance, eventInstances]
	);

	const eventShareLink = useMemo(() => `${HOME_REDIRECT_URL}${eventPageUrl}`, [eventPageUrl]);

	const hostPageUrl = useMemo(
		() => eventData?.author?.userName && makeHostLink({ userName: eventData.author.userName }),
		[eventData?.author?.userName, makeHostLink]
	);
	const coHostPageUrl = useMemo(
		() => eventData?.coHost?.userName && makeHostLink({ userName: eventData.coHost.userName }),
		[eventData?.coHost?.userName, makeHostLink]
	);

	const isHost = useMemo(
		() => eventData?.author && currentUser && eventData.author.id === currentUser.id,
		[eventData?.author, currentUser]
	);
	const isCoHost = useMemo(
		() => eventData?.coHost && currentUser && eventData.coHost.id === currentUser.id,
		[eventData?.coHost, currentUser]
	);

	const isRsvpListHidden = useMemo(
		() => eventData?.isRsvpListHidden && !isHost && !isCoHost,
		[eventData?.isRsvpListHidden, isHost, isCoHost]
	);

	const participantsCount = useMemo(() => eventData?.participantsCount ?? 0, [eventData?.participantsCount]);

	const isPastEvent = useMemo(() => {
		if (!eventData || !eventData.instance || !eventData.shortLocalInstance) {
			return false;
		}

		return isEventPast(eventData as EventInstanceDto);
	}, [eventData]);

	const isPaidEvent = useMemo(() => (!!eventData && eventData.isPaid && !eventData.isDonationBased), [eventData]);

	const canCheckoutAfterEndDate = useMemo(() => canCheckoutEventAfterEndDate(eventData as EventInstanceDto), [eventData]);

	const priceString = useMemo(
		() => (eventData?.isDonationBased ? 'Donation-based' : getEventPriceRangeString(eventData?.prices)),
		[eventData?.isDonationBased, eventData?.prices]
	);

	const leaders = useMemo(() => eventData?.eventLeaders || [], [eventData]);
	const sponsors = useMemo(() => eventData?.eventSponsors || [], [eventData]);

	return {
		eventId,
		instance,
		eventAlias,
		shortLocalInstance,
		isHost,
		isCoHost,
		isPaidEvent,
		isRsvpListHidden,
		participantsCount,
		host: eventData?.author,
		coHost: eventData?.coHost,
		isPastEvent,
		canCheckoutAfterEndDate,
		priceString,
		eventPageUrl,
		eventShareLink,
		hostPageUrl,
		coHostPageUrl,
		leaders,
		sponsors,
	};
};
